import Joi from 'joi';

import { TaxRateTypeEnum } from '../../../../shared/enums/tax-rate-type.enum';
import { createTagsSchema } from '../../../tags/validators/schemas/create-tags.schema';
import { VehicleRoadTaxRecordCategoryEnum } from '../../enums/vehicle-road-tax-record-category.enum';

/**
 * Schema for existing vehicle road tax record validation.
 */
export const updateVehicleRoadTaxSchema = () =>
  Joi.object({
    id: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|vehicle-road-tax-id|cannot-be-null',
      }),
      otherwise: Joi.number().required().messages({
        'any.required': 'validations|vehicle-road-tax-id|required',
        'number.base': 'validations|vehicle-road-tax-id|must-be-number',
      }),
    }),

    code: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid': 'validations|vehicle-road-tax-code|cannot-be-null',
      }),
      otherwise: Joi.string().required().messages({
        'any.required': 'validations|vehicle-road-tax-code|required',
        'string.base': 'validations|vehicle-road-tax-code|must-be-string',
        'string.empty': 'validations|vehicle-road-tax-code|cannot-be-empty',
      }),
    }),

    recordCategory: Joi.alternatives().conditional(Joi.any().valid(null), {
      then: Joi.invalid(null).messages({
        'any.invalid':
          'validations|vehicle-road-tax-record-category|cannot-be-null',
      }),
      otherwise: Joi.string()
        .valid(...Object.values(VehicleRoadTaxRecordCategoryEnum))
        .required()
        .messages({
          'any.required':
            'validations|vehicle-road-tax-record-category|required',
          'any.only':
            'validations|vehicle-road-tax-record-category|invalid-value',
          'string.base':
            'validations|vehicle-road-tax-record-category|must-be-string',
          'string.empty':
            'validations|vehicle-road-tax-record-category|cannot-be-empty',
        }),
    }),

    vehicleId: Joi.when('recordCategory', {
      is: VehicleRoadTaxRecordCategoryEnum.VEHICLE,
      then: Joi.alternatives().conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-road-tax-vehicle-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|vehicle-road-tax-vehicle-id|required',
          'number.base':
            'validations|vehicle-road-tax-vehicle-id|must-be-number',
        }),
      }),
      otherwise: Joi.allow(null),
    }),

    startsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid':
            'validations|vehicle-road-tax-starts-at|cannot-be-null',
        }),
        otherwise: Joi.date().required().messages({
          'any.required': 'validations|vehicle-road-tax-starts-at|required',
          'date.base':
            'validations|vehicle-road-tax-starts-at|must-be-a-valid-date',
        }),
      })
      .prefs({ abortEarly: true }),

    endsAt: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-road-tax-ends-at|cannot-be-null',
        }),
        otherwise: Joi.date().greater(Joi.ref('startsAt')).required().messages({
          'any.required': 'validations|vehicle-road-tax-ends-at|required',
          'date.base':
            'validations|vehicle-road-tax-ends-at|must-be-a-valid-date',
          'date.greater':
            'validations|vehicle-road-tax-ends-at|cannot-be-before-starts-at',
        }),
      })
      .prefs({ abortEarly: true }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-road-tax-notes|must-be-string',
    }),

    externalReference: Joi.string().allow(null, '').messages({
      'string.base':
        'validations|vehicle-road-tax-external-reference|must-be-string',
    }),

    taxRateType: Joi.string()
      .valid(...Object.values(TaxRateTypeEnum))
      .optional()
      .empty(null)
      .messages({
        'any.only': 'validations|vehicle-road-tax-tax-rate-type|invalid-value',
      }),

    taxRate: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-road-tax-tax-rate|must-be-number',
    }),

    isTaxIncluded: Joi.boolean().optional().empty(null),

    amount: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-road-tax-amount|must-be-number',
    }),

    totalNet: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-road-tax-total-net|must-be-number',
    }),

    totalTax: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-road-tax-total-tax|must-be-number',
    }),

    totalGross: Joi.number().allow(null).messages({
      'number.base': 'validations|vehicle-road-tax-total-gross|must-be-number',
    }),

    tags: createTagsSchema().messages({
      'array.base': 'validations|vehicle-road-tax-tags|invalid-value',
    }),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
